import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';

// Menu
export interface Menu {
  headTitle?: string;
  headTitle2?: string;
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeValue?: string;
  badgeClass?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  Menusub?: boolean;
  target?: boolean;
  locked?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(
    window.innerWidth
  );

  // Search Box
  public search: boolean = false;

  // Language
  public language: boolean = false;

  // Mega Menu
  public megaMenu: boolean = false;
  public levelMenu: boolean = false;
  public megaMenuColapse: boolean = window.innerWidth < 1199 ? true : false;

  // Collapse Sidebar
  public collapseSidebar: boolean = window.innerWidth < 991 ? true : false;

  // For Horizontal Layout Mobile
  public horizontal: boolean = window.innerWidth < 991 ? false : true;

  // Full screen
  public fullScreen: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private http: HttpClient
  ) {
    this.setScreenWidth(window.innerWidth);
    fromEvent(window, 'resize')
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < 991) {
          this.collapseSidebar = true;
          this.megaMenu = false;
          this.levelMenu = false;
        }
        if (evt.target.innerWidth < 1199) {
          this.megaMenuColapse = true;
        }
      });
    if (window.innerWidth < 991) {
      // Detect Route change sidebar close
      this.router.events.subscribe((event) => {
        this.collapseSidebar = true;
        this.megaMenu = false;
        this.levelMenu = false;
      });
    }

    const isExternal = window.location.pathname.startsWith('/external/');

    if (!isExternal && !this.authService.isAdmin() && !this.authService.isCrypto()) {
      this.checkApiCredentialsVisibility();
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next;
    this.unsubscriber.complete();
  }

  private withdrawMenu(): void {
    const withdraw = this.MENU_COMMON.find(
      (m) => m.title === 'common.withdraw'
    );
    if (!withdraw || !withdraw.children) {
      return;
    }

    if (withdraw.locked) {
      withdraw.path = undefined;
      withdraw.children = undefined;
      withdraw.type = 'link';
      withdraw.Menusub = false;
      return;
    }

    const origin = this.authService.getOriginCurrency();

    if (origin !== 'BRL') {
      const [firstChild] = withdraw.children;
      withdraw.locked = true;
      withdraw.path = firstChild?.path || '';
      withdraw.type = 'link';
      withdraw.Menusub = false;
      withdraw.children = undefined;
    }
  }

  private setCheckoutConfigVisibility(): void {
    const origin = (this.authService.getOriginCurrency() || 'BRL').toUpperCase();

    if (origin === 'BRL') {
      this.MENU_COMMON = this.MENU_COMMON.filter(
        (m) => m.path !== '/checkout-config'
      );
    }
  }

  private depositMenu(): void {
    const depositMenu = this.MENU_COMMON.find(
      (m) => m.title === 'common.deposit'
    );
    if (!depositMenu || !depositMenu.children) {
      return;
    }

    const origin = this.authService.getOriginCurrency();

    if (origin === 'BRL') {
      depositMenu.children = depositMenu.children.filter(
        (c) => c.path !== '/cashIn'
      );
    } else {
      depositMenu.children = depositMenu.children.filter(
        (c) => c.path === '/cashIn'
      );

      if (depositMenu.children.length === 1) {
        const [only] = depositMenu.children;
        depositMenu.path = only.path;
        depositMenu.type = 'link';
        depositMenu.Menusub = false;
        depositMenu.children = undefined;
      }
    }
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }


  public MENU_COMMON: Menu[] = [
    {
      headTitle: 'common.main',
    },
    {
      title: 'common.dashboard',
      icon: 'fe fe-file-text',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/reports',
    },
    {
      title: 'common.subAccounts',
      icon: 'fe fe-briefcase',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/sub-accounts',
    },
    {
      title: 'common.transactions',
      icon: 'fe fe-edit',
      type: 'sub',
      Menusub: true,
      active: false,
      children: [
        { path: '/extrato', title: 'common.movement', type: 'link' },
        { path: '/resumo', title: 'common.summary', type: 'link' },
        { path: '/infracoes', title: 'common.infractions', type: 'link' },
      ],
    },
    {
      title: 'common.commissions',
      icon: 'fe fe-percent',
      type: 'sub',
      Menusub: true,
      active: false,
      children: [
        { path: '/comissoes', title: 'common.billing', type: 'link' },
        { path: '/carteira-comissoes', title: 'common.wallet', type: 'link' },
      ],
    },
    {
      title: 'common.deposit',
      icon: 'fe fe-log-in',
      type: 'sub',
      Menusub: true,
      active: false,
      children: [
        { path: '/deposito', title: 'common.qrCode', type: 'link' },
        { path: '/depositoPix', title: 'common.pixKey', type: 'link' },
        { path: '/cashIn', title: 'Cash In', type: 'link' },
      ],
    },
    {
      title: 'common.withdraw',
      icon: 'fe fe-log-out',
      type: 'sub',
      Menusub: true,
      active: false,
      children: [
        { path: '/transferencia', title: 'common.pixKey', type: 'link' },
        { path: '/saque-copia-cola', title: 'common.copyPaste', type: 'link' },
        { path: '/saque-boleto', title: 'common.boletoWithdrawal', type: 'link' },
      ],
    },
    {
      title: 'common.transferBalance',
      icon: 'icon-wallet',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/transfer-balance',
    },
    {
      title: 'common.approveWithdrawals',
      icon: 'fe fe-unlock',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/approve-withdraw',
    },
    {
      title: 'common.ggrReserve',
      icon: 'fe fe-dollar-sign',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/reserva-financeira',
    },
    {
      title: 'common.cryptoassets',
      icon: 'fa fa-bitcoin',
      type: 'sub',
      Menusub: true,
      active: false,
      children: [
        { path: '/criptomoeda', title: 'common.withdraw', type: 'link' },
        { path: '/wallet', title: 'common.cryptoWallet', type: 'link' },
      ]
    },
    {
      headTitle: 'common.configurations',
    },
    {
      title: 'common.account',
      icon: 'fe fe-user',
      type: 'sub',
      Menusub: true,
      active: false,
      children: [
        { path: '/account/view', title: 'common.information', type: 'link' },
        { path: '/webhook', title: 'common.webHook', type: 'link' },
        { path: '/api-credentials', title: 'common.apiCredentials', type: 'link' },
      ],
    },
    {
      title: 'common.users',
      icon: 'fe fe-user-plus',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/user-config',
    },
    {
      title: 'common.checkout',
      icon: 'fe fe-settings',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/checkout-config',
    },
  ];


  public MENU_ADMIN: Menu[] = [
    {
      title: 'Contas',
      icon: 'fe fe-user',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/account/account',
    },
    {
      title: 'Aprovar Saques',
      icon: 'fe fe-unlock',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/approve-withdraw-admin',
    },
  ];

  public MENU_CRYPTO: Menu[] = [
    {
      title: 'common.cryptoConversion',
      icon: 'fa fa-bitcoin',
      path: '/cripto',
      type: 'link',
      Menusub: false,
      active: false,
    },
    {
      title: 'common.account',
      icon: 'fe fe-user',
      path: '/account/view',
      type: 'link',
      Menusub: false,
      active: false,
    },
  ];

  private setNoCrypto() {
    if (this.authService.isNoCrypto()) {
      this.MENU_COMMON = this.MENU_COMMON.filter(
        (m) =>
          m.title !== 'common.cryptoassets' &&
          m.title !== 'common.cryptoConversion'
      );

      const accountMenu = this.MENU_COMMON.find(
        (m) => m.title === 'common.account'
      );

      if (accountMenu && accountMenu.children) {
        accountMenu.children = accountMenu.children.filter(
          (child) =>
            child.title !== 'common.cryptoWallet' &&
            child.title !== 'common.webHook'
        );
      }
    }

    return this.MENU_COMMON;
  }

  public getMenuItems() {
    if (this.authService.isAdmin()) {
      return this.MENU_ADMIN;
    }

    if (this.authService.isCrypto()) {
      return this.MENU_CRYPTO;
    }

    this.depositMenu();
    this.withdrawMenu();
    this.setNoCrypto();
    this.setCheckoutConfigVisibility();

    if (environment.isShowNewScreen) {
      const newhome = {
        title: 'common.home',
        icon: 'fe fe-home',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/dashboard/home',
      };

      this.MENU_COMMON.splice(1, 0, newhome);
    }

    return this.MENU_COMMON;
  }

  public MENUITEMS = this.getMenuItems();

  private hideApiCredentialsMenu(): void {
    const accountMenu = this.MENU_COMMON.find(
      (m) => m.title === 'common.account'
    );

    if (!accountMenu || !accountMenu.children) {
      return;
    }

    const filteredChildren = accountMenu.children.filter(
      (child) => child.path !== '/api-credentials'
    );

    if (filteredChildren.length === accountMenu.children.length) {
      return;
    }

    accountMenu.children = filteredChildren;

    this.items.next(this.MENU_COMMON);
  }

  private checkApiCredentialsVisibility(): void {
    this.http
      .get<any>(`${environment.apiUrl}/getaccountkeymulti`)
      .subscribe({
        next: (res) => {
          const list =
            res?.getAccountKeyOriginResponse ??
            [];

          const hasAccounts = Array.isArray(list) && list.length > 0;

          if (!hasAccounts) {
            this.hideApiCredentialsMenu();
          }
        },
        error: () => {
          this.hideApiCredentialsMenu();
        },
      });
  }

  items = new BehaviorSubject<Menu[]>(this.MENUITEMS);
}
